/**
 * Admin Production Messages moderation page (Slice 2 CP14 Phase 1).
 *
 * Structural clone of `Tts.tsx` adapted for `production_messages`:
 *   • read-only paginated list hitting
 *     `/v2/staging/production-messages/admin/queue.json`;
 *   • inline four-button review-state cell on every row (approve /
 *     hide / flag / unreview), mirroring Events.tsx + Tts.tsx;
 *   • filter row: `since` (ISO), `review_state` (select incl. "any"),
 *     `author` (case-preserved text — audit P16 pin), `page` (prev /
 *     next only);
 *   • hide / flag ALWAYS prompt for a reason and CP14 REQUIRES a
 *     non-empty trimmed reason at the parser — if the operator hits
 *     Cancel or leaves the box blank, the request is not sent;
 *   • approve / unreview never prompt (and the column clears to NULL
 *     through the Worker write-through rule);
 *   • no create / edit / entity-tagging affordance — production_messages
 *     rows are written by the ingest pipeline and there is no
 *     entity-link table in scope at the schema level.
 *
 * Idempotency: each click generates a fresh `crypto.randomUUID()`.
 * Same-key replay returns the prior receipt; same-key / different
 * to_state returns 409 (`production_message_idempotency_conflict`),
 * which we surface inline with a "retrying with fresh key" hint.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ProductionMessageReviewStateValidationError,
  ProductionMessagesAdminQueueItem,
  RecapReviewState,
} from '@fishstank/contracts'
import {
  fetchProductionMessagesAdminQueue,
  mutateProductionMessageReviewState,
} from '../lib/worker'
import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'

// crypto.randomUUID is widely available in modern browsers + jsdom;
// kept as a module-local helper for parity with Tts.tsx / Events.tsx so
// any future test harness can swap it.
function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

const BODY_PREVIEW_MAX = 140

function previewBody(body: string): string {
  if (body.length <= BODY_PREVIEW_MAX) return body
  return `${body.slice(0, BODY_PREVIEW_MAX - 1).trimEnd()}…`
}

// The four review-state options the operator may filter by. `any` is
// sent verbatim; the Worker parser translates it to "no review_state
// predicate".
type ReviewStateFilter = RecapReviewState | 'any'

const REVIEW_STATE_FILTER_OPTIONS: ReadonlyArray<{
  value: ReviewStateFilter
  label: string
}> = [
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'any', label: 'Any' },
]

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export function MessagesPage() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [reviewState, setReviewState] =
    useState<ReviewStateFilter>('unreviewed')
  const [authorInput, setAuthorInput] = useState<string>('')
  const [author, setAuthor] = useState<string | null>(null)
  const [sinceInput, setSinceInput] = useState<string>('')
  const [since, setSince] = useState<string | null>(null)

  const query = useQuery({
    queryKey: [
      'production-messages',
      'admin',
      'queue',
      { page, reviewState, author, since },
    ],
    queryFn: () =>
      fetchProductionMessagesAdminQueue({
        page,
        review_state: reviewState,
        author,
        since,
      }),
  })

  const result = query.data

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ['production-messages', 'admin', 'queue'],
    })

  function applyFilters() {
    setPage(1)
    const t = authorInput.trim()
    setAuthor(t.length > 0 ? t : null)
    const s = sinceInput.trim()
    setSince(s.length > 0 ? s : null)
  }

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">Messages</h1>
          <p className="mt-1 text-sm text-slate-400">
            Production-message moderation queue. Hide / flag require a
            reason; approve / unreview clear the stored reason. Author
            filter is case-significant.
          </p>
        </div>
      </header>

      <FilterRow
        reviewState={reviewState}
        onReviewStateChange={(v) => {
          setReviewState(v)
          setPage(1)
        }}
        authorInput={authorInput}
        onAuthorInputChange={setAuthorInput}
        sinceInput={sinceInput}
        onSinceInputChange={setSinceInput}
        onApply={applyFilters}
      />

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="production-messages"
        labels={{
          loading: 'Loading production messages…',
          errorTitle: "Couldn't load production messages.",
          empty: 'No production messages on this page.',
        }}
      >
        {(artifact) => (
          <>
            <AdminMessagesTable
              items={artifact.body.items}
              onChanged={invalidate}
            />
            <Pagination
              page={artifact.body.pagination.page}
              hasNext={artifact.body.pagination.has_next}
              totalReturned={artifact.body.pagination.total_returned}
              onChange={setPage}
            />
          </>
        )}
      </FetchedArtifact>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Filter row
// ────────────────────────────────────────────────────────────────────────────

function FilterRow({
  reviewState,
  onReviewStateChange,
  authorInput,
  onAuthorInputChange,
  sinceInput,
  onSinceInputChange,
  onApply,
}: {
  reviewState: ReviewStateFilter
  onReviewStateChange: (v: ReviewStateFilter) => void
  authorInput: string
  onAuthorInputChange: (v: string) => void
  sinceInput: string
  onSinceInputChange: (v: string) => void
  onApply: () => void
}) {
  return (
    <form
      data-testid="production-messages-filter-row"
      className="mb-4 flex flex-wrap items-end gap-2 rounded-md border border-slate-800 bg-fishstank-surface/40 p-3 text-xs"
      onSubmit={(e) => {
        e.preventDefault()
        onApply()
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          State
        </span>
        <select
          data-testid="production-messages-filter-review-state"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100"
          value={reviewState}
          onChange={(e) =>
            onReviewStateChange(e.target.value as ReviewStateFilter)
          }
        >
          {REVIEW_STATE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          Author (case-sensitive)
        </span>
        <input
          type="text"
          data-testid="production-messages-filter-author"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100 font-mono"
          value={authorInput}
          onChange={(e) => onAuthorInputChange(e.target.value)}
          placeholder="(any)"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          Since (ISO)
        </span>
        <input
          type="text"
          data-testid="production-messages-filter-since"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100 font-mono w-52"
          value={sinceInput}
          onChange={(e) => onSinceInputChange(e.target.value)}
          placeholder="default: last 24h"
        />
      </label>

      <button
        type="submit"
        data-testid="production-messages-filter-apply"
        className="rounded-sm border border-slate-700 bg-slate-900 px-3 py-1 text-slate-200 hover:bg-slate-800"
      >
        Apply
      </button>
    </form>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────────────────────────

function AdminMessagesTable({
  items,
  onChanged,
}: {
  items: readonly ProductionMessagesAdminQueueItem[]
  onChanged: () => void
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="production-messages-table"
        data-production-messages-count={items.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-20">ID</th>
            <th className="px-3 py-2 text-left font-medium">Body</th>
            <th className="px-3 py-2 text-left font-medium w-28">Author</th>
            <th className="px-3 py-2 text-left font-medium w-44">Occurred at</th>
            <th className="px-3 py-2 text-left font-medium w-40">Source</th>
            <th className="px-3 py-2 text-left font-medium w-28">Arc</th>
            <th className="px-3 py-2 text-left font-medium w-44">State</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {items.map((row) => (
            <AdminMessageRow
              key={row.id}
              row={row}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminMessageRow({
  row,
  onChanged,
}: {
  row: ProductionMessagesAdminQueueItem
  onChanged: () => void
}) {
  return (
    <tr
      data-testid="production-messages-row"
      data-production-message-id={row.id}
      data-review-state={row.review_state}
      className="hover:bg-slate-900/40"
    >
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.id}
      </td>
      <td className="px-3 py-1.5 text-slate-100">
        <span data-testid="production-messages-row-body">
          {previewBody(row.body)}
        </span>
        {row.hide_reason && (
          <div
            data-testid="production-messages-row-hide-reason"
            className="mt-1 text-[10px] uppercase tracking-wider text-slate-500"
          >
            reason:{' '}
            <span className="text-slate-400">{row.hide_reason}</span>
          </div>
        )}
      </td>
      <td
        data-testid="production-messages-row-author"
        className="px-3 py-1.5 text-slate-300 text-xs font-mono"
      >
        {row.author ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.occurred_at ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">
        {row.source_external_id}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.arc?.slug ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5">
        <MessagesReviewStateCell row={row} onChanged={onChanged} />
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Review-state cell
//
// Same 4-button shape as Tts.tsx / Events.tsx. Key deltas:
//   - hide/flag REQUIRE a non-empty reason at the parser level; the
//     UI enforces that the prompt returned a non-empty trimmed string
//     before firing the mutation. If the operator hits Cancel OR
//     supplies only whitespace, we bail without a network call and
//     surface the CP14 `production_message_hide_reason_required`
//     message locally so the guardrail is visible.
//   - narrows on ProductionMessageReviewStateValidationError
//     (`production_message_*` namespaced codes, incl. the two
//     hide_reason variants).
//   - testid prefix is `production-messages-review-state-*`.
// ────────────────────────────────────────────────────────────────────────────

const REVIEW_STATE_BUTTONS: ReadonlyArray<{
  state: RecapReviewState
  label: string
  className: string
  promptForReason: boolean
}> = [
  {
    state: 'approved',
    label: 'Approve',
    className:
      'rounded-sm border border-emerald-700 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-30',
    promptForReason: false,
  },
  {
    state: 'hidden',
    label: 'Hide',
    className:
      'rounded-sm border border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'flagged',
    label: 'Flag',
    className:
      'rounded-sm border border-amber-700 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'unreviewed',
    label: 'Unreview',
    className:
      'rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: false,
  },
]

function productionMessageReviewValidationMessage(
  err: ProductionMessageReviewStateValidationError,
): string {
  switch (err.code) {
    case 'invalid_review_state':
      return `Server rejected to_state="${err.received}".`
    case 'production_message_review_state_unchanged':
      return `Already at ${err.current}.`
    case 'production_message_idempotency_conflict':
      return `Conflict: that key already flipped to ${err.prior_to_state} (you sent ${err.requested_to_state}). Retrying with a fresh key.`
    case 'invalid_uuid':
      return 'Internal error: idempotency key invalid. Refresh and try again.'
    case 'invalid_enum':
      return `Invalid value for "${err.field}".`
    case 'production_message_not_found':
      return 'This production message no longer exists.'
    case 'production_message_hide_reason_required':
      return 'A non-empty reason is required to hide or flag.'
    case 'production_message_hide_reason_too_long':
      return `Reason too long — max ${err.max} characters.`
    default:
      return 'Review-state flip failed.'
  }
}

function MessagesReviewStateCell({
  row,
  onChanged,
}: {
  row: ProductionMessagesAdminQueueItem
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<RecapReviewState | null>(null)

  async function flip(to: RecapReviewState, promptForReason: boolean) {
    if (row.review_state === to) return
    let reason: string | null = null
    if (promptForReason) {
      const supplied = window.prompt(
        `Reason for flipping #${row.id} to ${to} (required):`,
        '',
      )
      if (supplied === null) return // operator hit Cancel
      const trimmed = supplied.trim()
      if (trimmed.length === 0) {
        setError('A non-empty reason is required to hide or flag.')
        return
      }
      reason = trimmed
    }

    setError(null)
    setPending(to)
    try {
      const res = await mutateProductionMessageReviewState(row.id, {
        body: {
          idempotency_key: newIdempotencyKey(),
          to_state: to,
          reason,
        },
      })
      if (res.kind === 'ok') {
        onChanged()
        return
      }
      if (res.kind === 'validation_error') {
        setError(productionMessageReviewValidationMessage(res.error))
        return
      }
      if (res.kind === 'csrf_error') {
        setError(`CSRF check failed: ${res.reason}`)
        return
      }
      if (res.kind === 'not_found') {
        setError('This production message no longer exists.')
        return
      }
      setError(res.message)
    } finally {
      setPending(null)
    }
  }

  return (
    <div
      data-testid="production-messages-review-state-cell"
      data-production-message-id={row.id}
      data-review-state={row.review_state}
      className="flex flex-col gap-1"
    >
      <span className="text-xs text-slate-300 uppercase">
        {row.review_state}
      </span>
      <div className="flex flex-wrap gap-1">
        {REVIEW_STATE_BUTTONS.map((b) => {
          const disabled = row.review_state === b.state || pending !== null
          return (
            <button
              key={b.state}
              type="button"
              data-testid="production-messages-review-state-button"
              data-production-message-id={row.id}
              data-to-state={b.state}
              disabled={disabled}
              onClick={() => {
                void flip(b.state, b.promptForReason)
              }}
              className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${b.className}`}
            >
              {pending === b.state ? '…' : b.label}
            </button>
          )
        })}
      </div>
      {error && (
        <span
          data-testid="production-messages-review-state-error"
          className="text-[10px] text-red-300"
        >
          {error}
        </span>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Pagination
//
// Same shape as Tts.tsx. Worker returns `page`, `has_next`,
// `total_returned`. No jump-to-page in Phase 1.
// ────────────────────────────────────────────────────────────────────────────

function Pagination({
  page,
  hasNext,
  totalReturned,
  onChange,
}: {
  page: number
  hasNext: boolean
  totalReturned: number
  onChange: (page: number) => void
}) {
  const prevDisabled = page <= 1
  const nextDisabled = !hasNext
  return (
    <div
      data-testid="production-messages-pagination"
      data-page={page}
      data-has-next={hasNext}
      className="mt-4 flex items-center justify-between text-xs text-slate-400"
    >
      <span>
        Page {page} · {totalReturned} row{totalReturned === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="production-messages-pagination-prev"
          disabled={prevDisabled}
          onClick={() => onChange(page - 1)}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          ← Prev
        </button>
        <button
          type="button"
          data-testid="production-messages-pagination-next"
          disabled={nextDisabled}
          onClick={() => onChange(page + 1)}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          Next →
        </button>
      </div>
    </div>
  )
}
